import { Edge, Node, ViewportPortal, useReactFlow } from "@xyflow/react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ContentCutOutlinedIcon from "@mui/icons-material/ContentCutOutlined";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { isEditableElement } from "../../hooks/UseShortcutsHook";
import { SequenceCommentZone } from "../../models/interactions/Sequence";
import { SequenceTextbox } from "../../models/interactions/Sequence";
import { SequenceNodeData } from "./SequenceNode";
import { CopyPasteContext } from "../training_timeline/CopyPasteContext";
import {
  getSelectionInputMode,
  SequenceCanvasItem,
  SelectionInputMode,
} from "./SequenceCanvasSelection.hook";

const DEFAULT_ZONE_COLOR = "#E8A735";
const COMMENT_ZONE_PADDING_LEFT = 88;
const COMMENT_ZONE_PADDING_RIGHT = 88;
const COMMENT_ZONE_PADDING_TOP = 40;
const COMMENT_ZONE_PADDING_BOTTOM = 88;
const COMMENT_ZONE_MIN_WIDTH = 420;
const COMMENT_ZONE_MIN_HEIGHT = 260;
const NODE_VISUAL_MARGIN_LEFT = 28;
const NODE_VISUAL_MARGIN_RIGHT = 28;
const NODE_VISUAL_MARGIN_TOP = 16;
const NODE_VISUAL_MARGIN_BOTTOM = 44;
const COMMENT_ZONE_RESIZE_HANDLE_SIZE = 18;
const COMMENT_ZONE_BORDER_DRAG_THICKNESS = 14;
const COMMENT_ZONE_COLOR_PRESETS = [
  "#1ABC9C",
  "#3AC4FF",
  "#8FBC8F",
  "#E8A735",
  "#D35F5F",
  "#F97393",
  "#A78BFA",
  "#94A3B8",
] as const;

export function createCommentZoneFromNodes(
  selectedMeasuredNodes: Node<SequenceNodeData>[],
): SequenceCommentZone | null {
  if (selectedMeasuredNodes.length === 0) {
    return null;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  selectedMeasuredNodes.forEach((node) => {
    const bounds = getNodeBounds(node);
    minX = Math.min(minX, bounds.left);
    minY = Math.min(minY, bounds.top);
    maxX = Math.max(maxX, bounds.right);
    maxY = Math.max(maxY, bounds.bottom);
  });

  if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
    return null;
  }

  return {
    id: `comment-zone-${GetNewGuid()}`,
    position: {
      x: minX - COMMENT_ZONE_PADDING_LEFT,
      y: minY - COMMENT_ZONE_PADDING_TOP,
    },
    size: {
      width: Math.max(
        COMMENT_ZONE_MIN_WIDTH,
        maxX - minX + COMMENT_ZONE_PADDING_LEFT + COMMENT_ZONE_PADDING_RIGHT,
      ),
      height: Math.max(
        COMMENT_ZONE_MIN_HEIGHT,
        maxY - minY + COMMENT_ZONE_PADDING_TOP + COMMENT_ZONE_PADDING_BOTTOM,
      ),
    },
    label: "Comment",
  };
}

function withAlpha(hexColor: string, alphaHex: string) {
  const normalized = hexColor.trim();
  if (!normalized.startsWith("#")) {
    return hexColor;
  }

  if (normalized.length === 4) {
    const r = normalized[1];
    const g = normalized[2];
    const b = normalized[3];
    return `#${r}${r}${g}${g}${b}${b}${alphaHex}`;
  }

  if (normalized.length === 7) {
    return `${normalized}${alphaHex}`;
  }

  return hexColor;
}

type DragState = {
  zoneId: string;
  pointerId: number;
  mode: "move" | "resize";
  startFlowPoint: { x: number; y: number };
  initialZone: SequenceCommentZone;
  capturedNodeIds: string[];
  initialNodePositions: Map<string, { x: number; y: number }>;
  capturedTextboxIds: string[];
  initialTextboxPositions: Map<string, { x: number; y: number }>;
  previewNodes: Node<SequenceNodeData>[];
  previewCommentZones: SequenceCommentZone[];
  previewTextboxes: SequenceTextbox[];
};

type SequenceCommentZoneLayerProps = {
  sequenceId?: string;
  nodes: Node<SequenceNodeData>[];
  commentZones: SequenceCommentZone[];
  textboxes: SequenceTextbox[];
  selectedNodes: Node<SequenceNodeData>[];
  selectedCommentZoneIds: string[];
  onSelectItem: (
    item: SequenceCanvasItem,
    mode: SelectionInputMode,
    options?: { preserveIfSelected?: boolean },
  ) => void;
  onCreateCommentZone: (zone: SequenceCommentZone) => void;
  onUpdateCommentZone: (
    zoneId: string,
    updates: Partial<SequenceCommentZone>,
    persist?: boolean,
  ) => void;
  onDeleteCommentZones: (zoneIds: string[]) => void;
  setLocalNodes: (
    updater:
      | Node<SequenceNodeData>[]
      | ((current: Node<SequenceNodeData>[]) => Node<SequenceNodeData>[]),
  ) => void;
  setLocalCommentZones: (
    updater:
      | SequenceCommentZone[]
      | ((current: SequenceCommentZone[]) => SequenceCommentZone[]),
  ) => void;
  setLocalTextboxes: (
    updater:
      SequenceTextbox[] | ((current: SequenceTextbox[]) => SequenceTextbox[]),
  ) => void;
  persistCanvasState: (params: {
    nextNodes?: Node<SequenceNodeData>[];
    nextCommentZones?: SequenceCommentZone[];
    nextTextboxes?: SequenceTextbox[];
  }) => void;
};

function getNodeBounds(
  node: Node<SequenceNodeData>,
  measuredNode?: Node<SequenceNodeData>,
) {
  const width = measuredNode?.width ?? node.width ?? 256;
  const height = measuredNode?.height ?? node.height ?? 100;

  return {
    left: node.position.x - NODE_VISUAL_MARGIN_LEFT,
    top: node.position.y - NODE_VISUAL_MARGIN_TOP,
    right: node.position.x + width + NODE_VISUAL_MARGIN_RIGHT,
    bottom: node.position.y + height + NODE_VISUAL_MARGIN_BOTTOM,
    width,
    height,
  };
}

function isNodeFullyInsideZone(
  node: Node<SequenceNodeData>,
  zone: SequenceCommentZone,
  measuredNode?: Node<SequenceNodeData>,
) {
  const bounds = getNodeBounds(node, measuredNode);
  const zoneLeft = zone.position.x;
  const zoneTop = zone.position.y;
  const zoneRight = zone.position.x + zone.size.width;
  const zoneBottom = zone.position.y + zone.size.height;

  return (
    bounds.left >= zoneLeft &&
    bounds.top >= zoneTop &&
    bounds.right <= zoneRight &&
    bounds.bottom <= zoneBottom
  );
}

function isTextboxFullyInsideZone(
  textbox: SequenceTextbox,
  zone: SequenceCommentZone,
) {
  const textboxRight = textbox.position.x + textbox.size.width;
  const textboxBottom = textbox.position.y + textbox.size.height;
  const zoneLeft = zone.position.x;
  const zoneTop = zone.position.y;
  const zoneRight = zone.position.x + zone.size.width;
  const zoneBottom = zone.position.y + zone.size.height;

  return (
    textbox.position.x >= zoneLeft &&
    textbox.position.y >= zoneTop &&
    textboxRight <= zoneRight &&
    textboxBottom <= zoneBottom
  );
}

export default function SequenceCommentZoneLayer({
  sequenceId = "",
  nodes,
  commentZones,
  textboxes,
  selectedNodes,
  selectedCommentZoneIds,
  onSelectItem,
  onCreateCommentZone,
  onUpdateCommentZone,
  onDeleteCommentZones,
  setLocalNodes,
  setLocalCommentZones,
  setLocalTextboxes,
  persistCanvasState,
}: SequenceCommentZoneLayerProps) {
  const copyPasteContext = useContext(CopyPasteContext);
  const pendingSelection =
    copyPasteContext?.pendingCut?.kind === "sequenceSelection" &&
    copyPasteContext.pendingCut.source.sequenceId === sequenceId
      ? copyPasteContext.pendingCut.selection
      : null;
  const { getNodes, screenToFlowPosition } = useReactFlow<
    Node<SequenceNodeData>,
    Edge
  >();
  const dragStateRef = useRef<DragState | null>(null);
  const [openColorPickerZoneId, setOpenColorPickerZoneId] = useState<
    string | null
  >(null);
  const measuredNodesById = useMemo<Map<string, Node<SequenceNodeData>>>(
    () =>
      new Map<string, Node<SequenceNodeData>>(
        getNodes().map((node) => [node.id, node]),
      ),
    [getNodes, nodes, commentZones],
  );

  useEffect(() => {
    const handleWindowPointerDown = () => {
      setOpenColorPickerZoneId(null);
    };

    window.addEventListener("pointerdown", handleWindowPointerDown);
    return () =>
      window.removeEventListener("pointerdown", handleWindowPointerDown);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() !== "c" ||
        !event.altKey ||
        isEditableElement(event.target)
      ) {
        return;
      }

      if (selectedNodes.length === 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const selectedNodeIds = new Set(selectedNodes.map((node) => node.id));
      const selectedMeasuredNodes = Array.from(selectedNodeIds)
        .map((nodeId) => measuredNodesById.get(nodeId))
        .filter((node): node is Node<SequenceNodeData> => Boolean(node));
      if (selectedMeasuredNodes.length === 0) {
        return;
      }

      const zone = createCommentZoneFromNodes(selectedMeasuredNodes);
      if (!zone) {
        return;
      }

      onCreateCommentZone(zone);
      onSelectItem({ kind: "commentZone", id: zone.id }, "replace");
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    measuredNodesById,
    nodes,
    onCreateCommentZone,
    selectedNodes,
    onSelectItem,
  ]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      const currentFlowPoint = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const deltaX = currentFlowPoint.x - dragState.startFlowPoint.x;
      const deltaY = currentFlowPoint.y - dragState.startFlowPoint.y;

      const nextCommentZones = commentZones.map((zone) =>
        zone.id === dragState.zoneId
          ? {
              ...zone,
              ...(dragState.mode === "move"
                ? {
                    position: {
                      x: dragState.initialZone.position.x + deltaX,
                      y: dragState.initialZone.position.y + deltaY,
                    },
                  }
                : {
                    size: {
                      width: Math.max(
                        COMMENT_ZONE_MIN_WIDTH,
                        dragState.initialZone.size.width + deltaX,
                      ),
                      height: Math.max(
                        COMMENT_ZONE_MIN_HEIGHT,
                        dragState.initialZone.size.height + deltaY,
                      ),
                    },
                  }),
            }
          : zone,
      );

      const nextNodes = nodes.map((node) => {
        if (dragState.mode !== "move") {
          return node;
        }
        if (!dragState.capturedNodeIds.includes(node.id)) {
          return node;
        }

        const initialPosition = dragState.initialNodePositions.get(node.id);
        if (!initialPosition) {
          return node;
        }

        return {
          ...node,
          position: {
            x: initialPosition.x + deltaX,
            y: initialPosition.y + deltaY,
          },
        };
      });

      const nextTextboxes = textboxes.map((textbox) => {
        if (dragState.mode !== "move") {
          return textbox;
        }
        if (!dragState.capturedTextboxIds.includes(textbox.id)) {
          return textbox;
        }

        const initialPosition = dragState.initialTextboxPositions.get(
          textbox.id,
        );
        if (!initialPosition) {
          return textbox;
        }

        return {
          ...textbox,
          position: {
            x: initialPosition.x + deltaX,
            y: initialPosition.y + deltaY,
          },
        };
      });

      dragState.previewCommentZones = nextCommentZones;
      dragState.previewNodes = nextNodes;
      dragState.previewTextboxes = nextTextboxes;
      setLocalCommentZones(nextCommentZones);
      setLocalNodes(nextNodes);
      setLocalTextboxes(nextTextboxes);
    };

    const handlePointerUp = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      dragStateRef.current = null;
      persistCanvasState({
        nextNodes: dragState.previewNodes,
        nextCommentZones: dragState.previewCommentZones,
        nextTextboxes: dragState.previewTextboxes,
      });
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [
    commentZones,
    nodes,
    persistCanvasState,
    screenToFlowPosition,
    setLocalCommentZones,
    setLocalNodes,
    setLocalTextboxes,
    textboxes,
  ]);

  const handleZonePointerDown =
    (zoneId: string) => (event: React.PointerEvent<HTMLDivElement>) => {
      if ((event.target as HTMLElement).closest("input, textarea")) {
        return;
      }

      event.stopPropagation();
      onSelectItem(
        { kind: "commentZone", id: zoneId },
        getSelectionInputMode(event),
        { preserveIfSelected: true },
      );
    };

  const handleZoneDragStart =
    (zone: SequenceCommentZone) =>
    (event: React.PointerEvent<HTMLDivElement>) => {
      if ((event.target as HTMLElement).closest("input, textarea")) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      onSelectItem(
        { kind: "commentZone", id: zone.id },
        getSelectionInputMode(event),
        { preserveIfSelected: true },
      );

      const startFlowPoint = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const capturedNodeIds = nodes
        .filter((node) =>
          isNodeFullyInsideZone(node, zone, measuredNodesById.get(node.id)),
        )
        .map((node) => node.id);
      const initialNodePositions = new Map(
        nodes
          .filter((node) => capturedNodeIds.includes(node.id))
          .map((node) => [node.id, { ...node.position }]),
      );
      const capturedTextboxIds = textboxes
        .filter((textbox) => isTextboxFullyInsideZone(textbox, zone))
        .map((textbox) => textbox.id);
      const initialTextboxPositions = new Map(
        textboxes
          .filter((textbox) => capturedTextboxIds.includes(textbox.id))
          .map((textbox) => [textbox.id, { ...textbox.position }]),
      );

      dragStateRef.current = {
        zoneId: zone.id,
        pointerId: event.pointerId,
        mode: "move",
        startFlowPoint,
        initialZone: {
          ...zone,
          position: { ...zone.position },
          size: { ...zone.size },
        },
        capturedNodeIds,
        initialNodePositions,
        capturedTextboxIds,
        initialTextboxPositions,
        previewNodes: nodes,
        previewCommentZones: commentZones,
        previewTextboxes: textboxes,
      };
    };

  const renderZoneBorderDragHandle = (
    zone: SequenceCommentZone,
    side: "top" | "right" | "bottom" | "left",
  ) => {
    const sharedStyle = {
      position: "absolute" as const,
      pointerEvents: "auto" as const,
      touchAction: "none" as const,
      background: "transparent",
    };

    if (side === "top") {
      return (
        <div
          key={`${zone.id}-drag-top`}
          className="cursor-move"
          style={{
            ...sharedStyle,
            left: 0,
            top: 0,
            width: "100%",
            height: COMMENT_ZONE_BORDER_DRAG_THICKNESS,
          }}
          data-testid={`comment-zone-border-top-${zone.id}`}
          onPointerDown={handleZoneDragStart(zone)}
        />
      );
    }

    if (side === "right") {
      return (
        <div
          key={`${zone.id}-drag-right`}
          className="cursor-move"
          style={{
            ...sharedStyle,
            right: 0,
            top: 0,
            width: COMMENT_ZONE_BORDER_DRAG_THICKNESS,
            height: "100%",
          }}
          data-testid={`comment-zone-border-right-${zone.id}`}
          onPointerDown={handleZoneDragStart(zone)}
        />
      );
    }

    if (side === "bottom") {
      return (
        <div
          key={`${zone.id}-drag-bottom`}
          className="cursor-move"
          style={{
            ...sharedStyle,
            left: 0,
            bottom: 0,
            width: "100%",
            height: COMMENT_ZONE_BORDER_DRAG_THICKNESS,
          }}
          data-testid={`comment-zone-border-bottom-${zone.id}`}
          onPointerDown={handleZoneDragStart(zone)}
        />
      );
    }

    return (
      <div
        key={`${zone.id}-drag-left`}
        className="cursor-move"
        style={{
          ...sharedStyle,
          left: 0,
          top: 0,
          width: COMMENT_ZONE_BORDER_DRAG_THICKNESS,
          height: "100%",
        }}
        data-testid={`comment-zone-border-left-${zone.id}`}
        onPointerDown={handleZoneDragStart(zone)}
      />
    );
  };

  const handleZoneResizeStart =
    (zone: SequenceCommentZone) =>
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      onSelectItem(
        { kind: "commentZone", id: zone.id },
        getSelectionInputMode(event),
        { preserveIfSelected: true },
      );

      dragStateRef.current = {
        zoneId: zone.id,
        pointerId: event.pointerId,
        mode: "resize",
        startFlowPoint: screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        }),
        initialZone: {
          ...zone,
          position: { ...zone.position },
          size: { ...zone.size },
        },
        capturedNodeIds: [],
        initialNodePositions: new Map(),
        capturedTextboxIds: [],
        initialTextboxPositions: new Map(),
        previewNodes: nodes,
        previewCommentZones: commentZones,
        previewTextboxes: textboxes,
      };
    };

  return (
    <ViewportPortal>
      <style>{`
        .react-flow__viewport-portal {
          z-index: 1;
        }
        .react-flow__nodes {
          z-index: 2;
        }
      `}</style>
      {commentZones.map((zone) => {
        const selected = selectedCommentZoneIds.includes(zone.id);
        const isPendingCut = Boolean(
          pendingSelection?.commentZoneIds.includes(zone.id),
        );
        const borderColor = zone.color ?? DEFAULT_ZONE_COLOR;
        const fillColor = withAlpha(borderColor, selected ? "1F" : "12");

        return (
          <div
            key={zone.id}
            className={`absolute pointer-events-auto rounded-[12px] bg-transparent transition-opacity duration-150 motion-reduce:transition-none ${
              isPendingCut ? "opacity-60" : ""
            }`}
            style={{
              left: zone.position.x,
              top: zone.position.y,
              width: zone.size.width,
              height: zone.size.height,
              zIndex: 0,
            }}
            data-testid={`comment-zone-${zone.id}`}
            role="group"
            aria-selected={selected}
            onPointerDown={handleZonePointerDown(zone.id)}
            aria-label={
              isPendingCut
                ? "Cut pending. Paste to move or press Escape to cancel."
                : undefined
            }
          >
            {isPendingCut && (
              <span
                className="pointer-events-none absolute -top-3 right-3 z-20 flex h-6 w-6 items-center justify-center rounded-[6px] border border-creator-primary bg-creator-surface text-[16px] text-creator-text-main"
                aria-label="Cut pending. Paste to move or press Escape to cancel."
              >
                <ContentCutOutlinedIcon fontSize="inherit" aria-hidden="true" />
              </span>
            )}
            <div
              className="absolute inset-0 rounded-[12px] pointer-events-none"
              style={{
                border: `2px solid ${borderColor}`,
                backgroundColor: fillColor,
                boxShadow: selected
                  ? `0 0 0 3px ${borderColor}40, 0 4px 20px ${borderColor}55`
                  : undefined,
                zIndex: -1,
              }}
            />
            {[
              renderZoneBorderDragHandle(zone, "top"),
              renderZoneBorderDragHandle(zone, "right"),
              renderZoneBorderDragHandle(zone, "bottom"),
              renderZoneBorderDragHandle(zone, "left"),
            ]}
            <div
              className="absolute left-4 right-12 top-4 flex items-start gap-2 px-0 py-0 cursor-move pointer-events-auto bg-transparent"
              style={{
                touchAction: "none",
              }}
              data-testid={`comment-zone-header-${zone.id}`}
              onPointerDown={handleZoneDragStart(zone)}
            >
              <label
                className="relative mt-[4px] flex h-3 w-3 shrink-0 cursor-pointer items-center justify-center"
                onPointerDown={(event) => {
                  event.stopPropagation();
                  setOpenColorPickerZoneId((current) =>
                    current === zone.id ? null : zone.id,
                  );
                }}
                title="Comment zone color"
              >
                <span
                  className="block h-3 w-3 rounded-full border border-white/20"
                  style={{ backgroundColor: borderColor }}
                />
              </label>
              {openColorPickerZoneId === zone.id && (
                <div
                  className="absolute left-0 top-full mt-2 flex gap-1 rounded-[10px] border bg-creator-surface px-2 py-2 shadow-[0_10px_24px_rgba(0,0,0,0.28)]"
                  style={{ borderColor }}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  {COMMENT_ZONE_COLOR_PRESETS.map((presetColor) => (
                    <button
                      key={presetColor}
                      type="button"
                      className="h-4 w-4 rounded-full border border-white/20"
                      style={{ backgroundColor: presetColor }}
                      aria-label={`Set comment zone color to ${presetColor}`}
                      title={presetColor}
                      onClick={(event) => {
                        event.stopPropagation();
                        onUpdateCommentZone(
                          zone.id,
                          { color: presetColor },
                          true,
                        );
                        setOpenColorPickerZoneId(null);
                      }}
                    />
                  ))}
                </div>
              )}
              <textarea
                rows={1}
                className="w-full resize-none overflow-hidden bg-transparent outline-none text-[13px] font-semibold leading-5 text-slate-800 placeholder:text-slate-600"
                value={zone.label}
                placeholder="Comment"
                onPointerDown={(event) => event.stopPropagation()}
                onFocus={() =>
                  onSelectItem({ kind: "commentZone", id: zone.id }, "replace")
                }
                onInput={(event) => {
                  const target = event.currentTarget;
                  target.style.height = "0px";
                  target.style.height = `${target.scrollHeight}px`;
                }}
                onChange={(event) =>
                  onUpdateCommentZone(
                    zone.id,
                    { label: event.target.value },
                    false,
                  )
                }
                onBlur={(event) =>
                  onUpdateCommentZone(
                    zone.id,
                    { label: event.target.value },
                    true,
                  )
                }
              />
            </div>
            <button
              type="button"
              className="absolute top-3 right-3 pointer-events-auto bg-transparent text-slate-800 hover:text-slate-600"
              aria-label={`Delete comment zone ${zone.label || zone.id}`}
              title="Delete comment zone"
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                onDeleteCommentZones([zone.id]);
              }}
            >
              <CloseIcon fontSize="small" />
            </button>
            <div
              className="absolute rounded-full border pointer-events-auto bg-white/85 shadow-sm cursor-se-resize opacity-0"
              style={{
                width: COMMENT_ZONE_RESIZE_HANDLE_SIZE,
                height: COMMENT_ZONE_RESIZE_HANDLE_SIZE,
                right: -COMMENT_ZONE_RESIZE_HANDLE_SIZE / 2,
                bottom: -COMMENT_ZONE_RESIZE_HANDLE_SIZE / 2,
                borderColor,
                touchAction: "none",
              }}
              data-testid={`comment-zone-resize-${zone.id}`}
              onPointerDown={handleZoneResizeStart(zone)}
            />
          </div>
        );
      })}
    </ViewportPortal>
  );
}
